// Profile document categories, labels, and validation helpers.
// Used by both EMT-owner upload UI and employer read-only display.
// Designed to be flexible: this is a document checklist, NOT hardcoded legal compliance.

export type ProfileDocumentCategory =
  | "resume"
  | "emtCertification"
  | "stateLicense"
  | "nremtCertification"
  | "cprBls"
  | "driversLicense"
  | "rideTimeProof"
  | "clinicalHoursProof"
  | "iftExperienceProof"
  | "emergency911ExperienceProof"
  | "immunizationRecord"
  | "medicalClearance"
  | "backgroundCheck"
  | "drugScreen"
  | "drivingRecord"
  | "other";

export type ProfileDocumentStatus =
  | "uploaded"
  | "verified"
  | "expired"
  | "rejected"
  | "pending";

export type ProfileDocumentGroup =
  | "resume"
  | "certifications"
  | "experience"
  | "compliance"
  | "other";

export type ProfileDocumentCategoryMeta = {
  key: ProfileDocumentCategory;
  label: string;
  group: ProfileDocumentGroup;
  description?: string;
  /** If true, multiple uploads in this category are useful (e.g. multiple certifications). */
  allowMultiple: boolean;
};

export const PROFILE_DOCUMENT_CATEGORIES: ProfileDocumentCategoryMeta[] = [
  { key: "resume", label: "Resume / CV", group: "resume", description: "Your most recent resume. Replacing this updates your hireable profile.", allowMultiple: false },

  { key: "emtCertification", label: "EMT certification", group: "certifications", description: "Your primary EMT (Basic/Advanced/Paramedic) certification document.", allowMultiple: true },
  // Second on purpose (2026-09-23): a missing valid driver's license is one of the most common
  // disqualifiers in EMS hiring (one recruiter: ~30 of 80 screened candidates), so it sits right under
  // the EMT certification on the upload page and in the employer's document view, not last in the group.
  { key: "driversLicense", label: "Driver's license", group: "certifications", description: "Front and back of your driver's license. Required by most ambulance providers.", allowMultiple: true },
  { key: "stateLicense", label: "State license", group: "certifications", description: "Your State EMS Certification or license card (EMT, AEMT or Paramedic).", allowMultiple: true },
  { key: "nremtCertification", label: "NREMT certification", group: "certifications", description: "National Registry card (if applicable).", allowMultiple: false },
  { key: "cprBls", label: "CPR / BLS card", group: "certifications", description: "Healthcare-provider level CPR / BLS card.", allowMultiple: false },

  { key: "rideTimeProof", label: "Ride-time / field hours proof", group: "experience", description: "Document showing required ride-along or field hours.", allowMultiple: true },
  { key: "clinicalHoursProof", label: "Clinical hours proof", group: "experience", description: "Documentation of clinical rotation hours.", allowMultiple: true },
  { key: "iftExperienceProof", label: "IFT experience proof", group: "experience", description: "Proof of inter-facility transport experience.", allowMultiple: true },
  { key: "emergency911ExperienceProof", label: "911 experience proof", group: "experience", description: "Proof of 911 emergency response experience.", allowMultiple: true },

  // Medical (B1) and employer-arranged (B2) categories are uploaded only when an employer asks for them
  // (Request documents, after an offer for medical ones): they are not offered on the Account page.
  { key: "immunizationRecord", label: "Immunization record", group: "compliance", description: "Vaccination / immunization documentation. Only when an employer asks, after a job offer.", allowMultiple: true },
  { key: "medicalClearance", label: "Medical clearance", group: "compliance", description: "Recent medical / physical clearance from a provider. Only when an employer asks, after a job offer.", allowMultiple: true },
  { key: "backgroundCheck", label: "Background check", group: "compliance", description: "Your employer arranges the background check. Upload only if an employer asks.", allowMultiple: true },
  { key: "drugScreen", label: "Drug screen", group: "compliance", description: "Your employer arranges the drug screen. Upload only if an employer asks.", allowMultiple: true },
  { key: "drivingRecord", label: "MVR / driving record (optional)", group: "compliance", description: "Optional: your motor vehicle record. Employers usually check it themselves.", allowMultiple: true },

  { key: "other", label: "Other document", group: "other", description: "Anything else you'd like an employer to see (provide a clear label).", allowMultiple: true },
];

export const PROFILE_DOCUMENT_GROUPS: Array<{ key: ProfileDocumentGroup; title: string; description: string }> = [
  { key: "resume", title: "Resume", description: "Upload your most recent resume. Employers see this on your hireable profile." },
  { key: "certifications", title: "Credentials", description: "EMT certification, driver's license, State EMS Certification, NREMT, and CPR/BLS." },
  { key: "experience", title: "Experience Proof", description: "Optional proof of ride time, clinical hours, IFT, or 911 experience." },
  { key: "compliance", title: "Driving record (optional)", description: "Optional. Employers arrange background checks, drug screens and driving records themselves, and ask for any medical records only after a job offer." },
  { key: "other", title: "Other Documents", description: "Anything else you'd like to share." },
];

export const PROFILE_DOCUMENT_CATEGORY_MAP: Record<ProfileDocumentCategory, ProfileDocumentCategoryMeta> =
  PROFILE_DOCUMENT_CATEGORIES.reduce((acc, meta) => {
    acc[meta.key] = meta;
    return acc;
  }, {} as Record<ProfileDocumentCategory, ProfileDocumentCategoryMeta>);

/** Medical records (B1): only after a job offer, opened only by the hiring org's owner or a hiring manager the owner allows, never published. */
export const MEDICAL_DOCUMENT_CATEGORIES: readonly string[] = ['immunizationRecord', 'medicalClearance'];
export const isMedicalDocumentCategory = (key: unknown): boolean => typeof key === 'string' && MEDICAL_DOCUMENT_CATEGORIES.includes(key);
/** Uploaded only when an employer asks (B1 medical, B2 employer-arranged): not offered on the Account page. */
export const ON_REQUEST_ONLY_CATEGORIES: readonly string[] = [...MEDICAL_DOCUMENT_CATEGORIES, 'backgroundCheck', 'drugScreen'];

/**
 * The categories an upload page offers in a group. On-request-only categories are left out unless
 * `keep` lists one (an EMT who already has such a file still sees it, to view or delete).
 */
export function getCategoriesForGroup(group: ProfileDocumentGroup, keep: readonly string[] = PROFILE_DOCUMENT_CATEGORIES.map((c) => c.key)): ProfileDocumentCategoryMeta[] {
  return PROFILE_DOCUMENT_CATEGORIES.filter((c) => c.group === group && (!ON_REQUEST_ONLY_CATEGORIES.includes(c.key) || keep.includes(c.key)));
}

export function getCategoryLabel(key: string | undefined | null): string {
  if (!key) return "Document";
  return PROFILE_DOCUMENT_CATEGORY_MAP[key as ProfileDocumentCategory]?.label || key;
}

/**
 * Categories whose uploads are certification/license documents, and therefore get the SAME
 * automated document check as a certification added in the Certifications section
 * (functions/src/verifyCredentialDocument.ts — Azure extraction, name/DOB/expiry matching,
 * OpenAI fallback).
 *
 * These are exactly the categories the readiness Documentation pillar treats as the four required
 * credentials, which is why an unchecked upload here used to read as "✓ NREMT card uploaded".
 * Everything else in this list (immunization records, background checks, a resume) is not a
 * credential document and would be wrongly rejected by a credential check, so it is not sent.
 */
export const CREDENTIAL_DOCUMENT_CATEGORIES: ProfileDocumentCategory[] = [
  "emtCertification",
  "stateLicense",
  "nremtCertification",
  "cprBls",
  "driversLicense",
];

export function isCredentialDocumentCategory(key: string | undefined | null): boolean {
  return !!key && (CREDENTIAL_DOCUMENT_CATEGORIES as string[]).includes(key);
}

// Allowed upload types: PDF, DOC, DOCX, PNG, JPG, JPEG. 15 MB ceiling.
export const ALLOWED_DOCUMENT_EXTENSIONS = [".pdf", ".doc", ".docx", ".png", ".jpg", ".jpeg"];
export const ALLOWED_DOCUMENT_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/png",
  "image/jpeg",
  "image/jpg",
];

export const MAX_DOCUMENT_BYTES = 15 * 1024 * 1024;

export type DocumentValidationError =
  | { ok: true }
  | { ok: false; reason: "too_large" | "bad_type" | "empty"; message: string };

export function validateDocumentFile(file: File): DocumentValidationError {
  if (!file || file.size === 0) {
    return { ok: false, reason: "empty", message: "The selected file is empty." };
  }
  if (file.size > MAX_DOCUMENT_BYTES) {
    return {
      ok: false,
      reason: "too_large",
      message: `File is ${(file.size / (1024 * 1024)).toFixed(1)} MB. Max allowed is ${(MAX_DOCUMENT_BYTES / (1024 * 1024)).toFixed(0)} MB.`,
    };
  }
  const name = (file.name || "").toLowerCase();
  const ext = name.includes(".") ? name.slice(name.lastIndexOf(".")) : "";
  const mime = (file.type || "").toLowerCase();
  const okType =
    ALLOWED_DOCUMENT_MIME_TYPES.includes(mime) ||
    ALLOWED_DOCUMENT_EXTENSIONS.includes(ext);
  if (!okType) {
    return {
      ok: false,
      reason: "bad_type",
      message: "Unsupported file type. Use PDF, DOC, DOCX, PNG, JPG, or JPEG.",
    };
  }
  return { ok: true };
}

export function sanitizeFileName(name: string): string {
  return (name || "document").replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 120);
}

export function generateDocumentId(): string {
  return `doc_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}
